import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  History as HistoryIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
} from "@mui/icons-material";
import RotateLeftTwoToneIcon from "@mui/icons-material/RotateLeftTwoTone";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { User } from "../types/types.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

interface ProjectMemo {
  projectMemoId: number;
  content: string;
  createdHonbuId: number;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  sharedHonbuIds: number[];
  isUnread: boolean;
  isCompleted: boolean;
}

interface Props {
  todoListId: number;
  honbuId: number;
  roomId: number;
  roomMembers: User[];
  color: PaletteColor;
  onViewed?: () => void;
}

export function ProjectMemoPanel({
  todoListId,
  honbuId,
  roomId,
  roomMembers,
  color,
  onViewed,
}: Props) {
  const isDark = useDarkMode();
  const [memos, setMemos] = useState<ProjectMemo[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // 編集・削除用メニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMemo, setSelectedMemo] = useState<ProjectMemo | null>(null);

  // 共有設定ダイアログ（作成・編集兼用）
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTargetIds, setShareTargetIds] = useState<number[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const markViewed = useCallback(async () => {
    try {
      await fetch(
        `/api/todolists/${todoListId}/memos/viewed?honbuId=${honbuId}`,
        { method: "POST" },
      );
      onViewed?.();
    } catch (e) {
      console.error(e);
    }
  }, [todoListId, honbuId, onViewed]);

  const fetchMemos = useCallback(async (completed: boolean) => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `/api/todolists/${todoListId}/memos?honbuId=${honbuId}&roomId=${roomId}&showCompleted=${completed}`,
      );
      if (res.ok) {
        setMemos(await res.json());
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [todoListId, honbuId, roomId]);

  useEffect(() => {
    const init = async () => {
      await fetchMemos(false);
      await markViewed();
    };
    init();
  }, [todoListId, fetchMemos, markViewed]);

  useEffect(() => {
    fetchMemos(showHistory);
  }, [showHistory, fetchMemos]);

  const handleCompleteMemo = async (memo: ProjectMemo) => {
    try {
      await fetch(`/api/memos/${memo.projectMemoId}/complete`, { method: "PATCH" });
      fetchMemos(showHistory);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRestoreMemo = async (memo: ProjectMemo) => {
    try {
      await fetch(`/api/memos/${memo.projectMemoId}/restore`, { method: "PATCH" });
      fetchMemos(showHistory);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteMemoFromHistory = async (memo: ProjectMemo) => {
    try {
      await fetch(`/api/memos/${memo.projectMemoId}?honbuId=${honbuId}`, {
        method: "DELETE",
      });
      fetchMemos(showHistory);
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    memo: ProjectMemo,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedMemo(memo);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMemo(null);
  };

  const handleCreateMemo = async () => {
    if (!inputText.trim()) return;
    setIsSending(true);
    try {
      await fetch(`/api/todolists/${todoListId}/memos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Content: inputText,
          CreatedHonbuId: honbuId,
          SharedHonbuIds: shareTargetIds,
        }),
      });
      setInputText("");
      setShareTargetIds([]);
      fetchMemos(showHistory);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSending(false);
    }
  };

  const handleUpdateMemo = async () => {
    if (!selectedMemo || !inputText.trim()) return;
    try {
      await fetch(
        `/api/memos/${selectedMemo.projectMemoId}?honbuId=${honbuId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Content: inputText,
            SharedHonbuIds: shareTargetIds,
          }),
        },
      );
      fetchMemos(showHistory);
      handleCloseShareDialog();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteMemo = async () => {
    if (!selectedMemo) return;
    try {
      await fetch(
        `/api/memos/${selectedMemo.projectMemoId}?honbuId=${honbuId}`,
        { method: "DELETE" },
      );
      fetchMemos(showHistory);
      handleCloseMenu();
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenEdit = () => {
    if (!selectedMemo) return;
    setIsEditMode(true);
    setInputText(selectedMemo.content);
    setShareTargetIds(selectedMemo.sharedHonbuIds);
    setShowShareDialog(true);
    setAnchorEl(null);
  };

  const handleCloseShareDialog = () => {
    setShowShareDialog(false);
    if (isEditMode) {
      setSelectedMemo(null);
      setInputText("");
    }
  };

  const handleOpenShareSettingsForNew = () => {
    setIsEditMode(false);
    setShowShareDialog(true);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#1e1e1e]">
      {/* ヘッダー */}
      <div className="p-3 border-b border-gray-100 dark:border-white/10 flex items-center justify-between bg-gray-50/50 dark:bg-white/5">
        <h3 className="font-bold text-gray-700 dark:text-gray-200 text-sm flex items-center gap-2">
          <span
            className="w-1 h-4 rounded-full"
            style={{ backgroundColor: PaletteColorHex[color] }}
          />
          プロジェクトメモ
          {showHistory && (
            <span className="text-[10px] text-gray-400 font-normal">（履歴）</span>
          )}
        </h3>
        <div className="flex items-center">
          <Tooltip title={showHistory ? "通常表示に戻す" : "完了済みメモを表示"}>
            <IconButton
              size="small"
              onClick={() => setShowHistory((prev) => !prev)}
              color={showHistory ? "primary" : "default"}
              sx={{
                color:
                  isDark && !showHistory ? "rgba(255,255,255,0.7)" : undefined,
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="リロード">
            <IconButton
              size="small"
              onClick={() => fetchMemos(showHistory)}
              disabled={isLoading}
            >
              <RotateLeftTwoToneIcon
                fontSize="small"
                className={isLoading ? "animate-spin text-gray-400 dark:text-gray-500" : "text-gray-500 dark:text-gray-300"}
              />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* メモ一覧 */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {memos.length === 0 && !isLoading && (
          <div className="text-center text-gray-400 text-xs mt-10">
            {showHistory ? "完了済みメモはありません" : "メモはありません"}
          </div>
        )}
        {memos.map((memo) => {
          const isMyMemo = memo.createdHonbuId === honbuId;
          const isShared = memo.sharedHonbuIds.length > 0;
          return (
            <div
              key={memo.projectMemoId}
              className={`relative p-3 rounded-lg border ${
                showHistory
                  ? "bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 opacity-75"
                  : isMyMemo
                  ? "bg-blue-50/30 dark:bg-blue-500/10 border-blue-100 dark:border-blue-400/20"
                  : "bg-white dark:bg-white/[0.07] border-gray-100 dark:border-white/10 shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Avatar
                    sx={{ width: 20, height: 20, fontSize: 10 }}
                    className={isMyMemo ? "bg-blue-500" : "bg-gray-400"}
                  >
                    {memo.createdByName.charAt(0)}
                  </Avatar>
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-200 truncate max-w-[120px]">
                    {memo.createdByName}
                  </span>
                  {memo.isUnread && !showHistory && (
                    <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                      NEW
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400">
                    {new Date(memo.createdAt).toLocaleDateString()}{" "}
                    {new Date(memo.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* 通常モード：完了ボタン＋編集/削除メニュー（全員） */}
                {!showHistory && (
                  <div className="flex items-center -mr-2 -mt-2 flex-shrink-0">
                    <Tooltip title="完了にする">
                      <IconButton
                        size="small"
                        onClick={() => handleCompleteMemo(memo)}
                      >
                        <CheckCircleOutlineIcon
                          fontSize="small"
                          className="text-green-500"
                          sx={{ width: 16, height: 16 }}
                        />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, memo)}
                    >
                      <MoreVertIcon
                        fontSize="small"
                        className="w-4 h-4 dark:text-gray-300"
                      />
                    </IconButton>
                  </div>
                )}

                {/* 履歴モード：復元ボタン＋削除ボタン（全員） */}
                {showHistory && (
                  <div className="flex items-center -mr-2 -mt-2 flex-shrink-0">
                    <Tooltip title="復元する">
                      <IconButton
                        size="small"
                        onClick={() => handleRestoreMemo(memo)}
                      >
                        <RestoreFromTrashIcon
                          fontSize="small"
                          className="text-blue-500"
                          sx={{ width: 16, height: 16 }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="削除する">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMemoFromHistory(memo)}
                      >
                        <DeleteIcon
                          fontSize="small"
                          className="text-red-400"
                          sx={{ width: 16, height: 16 }}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words pl-7">
                {memo.content}
              </div>

              {/* 共有状態アイコン */}
              <div className="flex justify-end mt-1">
                {isShared ? (
                  <Tooltip title="限定共有">
                    <LockIcon sx={{ fontSize: 12 }} className="text-gray-400" />
                  </Tooltip>
                ) : (
                  <Tooltip title="全員に公開">
                    <PublicIcon sx={{ fontSize: 12 }} className="text-gray-300" />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 入力エリア（履歴モード時は非表示） */}
      {!showHistory && (
        <div className="p-3 border-t border-gray-200 dark:border-white/10 bg-white dark:bg-[#1e1e1e]">
          <div className="relative">
            <TextField
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              placeholder="メモを入力..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                style: { paddingRight: 70, fontSize: "0.875rem" },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: isDark ? "#e0e0e0" : undefined,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : undefined,
                  "& fieldset": {
                    borderColor: isDark ? "rgba(255,255,255,0.23)" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "rgba(255,255,255,0.4)" : undefined,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: isDark ? "rgba(255,255,255,0.5)" : undefined,
                  opacity: 1,
                },
              }}
            />
            <div className="absolute bottom-1 right-1 flex">
              {roomId !== 0 && (
                <Tooltip
                  title={
                    shareTargetIds.length > 0
                      ? "限定共有中"
                      : "共有設定 (デフォルト:全員)"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={handleOpenShareSettingsForNew}
                    color={shareTargetIds.length > 0 ? "warning" : "default"}
                    sx={{
                      color:
                        isDark && shareTargetIds.length === 0
                          ? "rgba(255,255,255,0.7)"
                          : undefined,
                    }}
                  >
                    {shareTargetIds.length > 0 ? (
                      <LockIcon fontSize="small" />
                    ) : (
                      <PeopleIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={handleCreateMemo}
                disabled={!inputText.trim() || isSending}
                color="primary"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
      )}

      {/* 通常モード：編集/削除メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            sx: {
              bgcolor: isDark ? "#2a2a2a" : undefined,
              color: isDark ? "#e0e0e0" : undefined,
            },
          },
        }}
      >
        <MenuItem onClick={handleOpenEdit}>
          <EditIcon
            fontSize="small"
            className="mr-2 text-gray-500 dark:text-gray-300"
          />
          <span className="text-sm">編集</span>
        </MenuItem>
        <MenuItem onClick={handleDeleteMemo}>
          <DeleteIcon fontSize="small" className="mr-2 text-red-500" />
          <span className="text-sm text-red-600">削除</span>
        </MenuItem>
      </Menu>

      {/* 共有設定ダイアログ */}
      <Dialog
        open={showShareDialog}
        onClose={handleCloseShareDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              bgcolor: isDark ? "#1e1e1e" : undefined,
              color: isDark ? "#e0e0e0" : undefined,
            },
          },
        }}
      >
        <DialogTitle className="text-sm font-bold">
          {isEditMode ? "メモを編集" : "共有設定"}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            borderColor: isDark ? "rgba(255,255,255,0.15)" : undefined,
          }}
        >
          {isEditMode && (
            <TextField
              label="メモ内容"
              multiline
              rows={4}
              fullWidth
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="mb-4"
              sx={{
                "& .MuiInputLabel-root": {
                  color: isDark ? "rgba(255,255,255,0.6)" : undefined,
                },
                "& .MuiOutlinedInput-root": {
                  color: isDark ? "#e0e0e0" : undefined,
                  "& fieldset": {
                    borderColor: isDark ? "rgba(255,255,255,0.23)" : undefined,
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? "rgba(255,255,255,0.4)" : undefined,
                  },
                },
              }}
            />
          )}

          <div className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2">
            共有範囲
          </div>
          <div className="mb-2">
            <Button
              variant={shareTargetIds.length === 0 ? "contained" : "outlined"}
              size="small"
              onClick={() => setShareTargetIds([])}
              fullWidth
              sx={{ mb: 1, textTransform: "none" }}
            >
              全員に公開 (デフォルト)
            </Button>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            または特定のメンバーを選択:
          </div>
          <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-white/15 rounded">
            {roomMembers
              .filter((m) => Number(m.id) !== honbuId)
              .map((member) => (
                <MenuItem
                  key={member.id}
                  dense
                  onClick={() => {
                    const id = Number(member.id);
                    setShareTargetIds((prev) =>
                      prev.includes(id)
                        ? prev.filter((x) => x !== id)
                        : [...prev, id],
                    );
                  }}
                >
                  <Checkbox
                    checked={shareTargetIds.includes(Number(member.id))}
                    size="small"
                    sx={{
                      color: isDark ? "rgba(255,255,255,0.6)" : undefined,
                    }}
                  />
                  <ListItemText
                    primary={member.name}
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                  />
                </MenuItem>
              ))}
            {roomMembers.length <= 1 && (
              <div className="p-2 text-xs text-gray-400 dark:text-gray-500 text-center">
                他のメンバーがいません
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={isEditMode ? handleUpdateMemo : handleCloseShareDialog}
            color="primary"
            variant="contained"
          >
            {isEditMode ? "更新" : "設定して戻る"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
